import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import PageLayout from "./PageLayout";
import { useCart } from "../context/CartContext";
import { mediaUrl } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `$${n.toFixed(2)}`;

const FIELDS = [
  { name: "name", label: "Full Name", placeholder: "Jane Doe", col: 2 },
  { name: "email", label: "Email", placeholder: "jane@example.com", type: "email", col: 2 },
  { name: "address", label: "Street Address", placeholder: "123 Grace Ave", col: 2 },
  { name: "city", label: "City", placeholder: "Atlanta", col: 1 },
  { name: "state", label: "State", placeholder: "GA", col: 1 },
  { name: "zip", label: "ZIP Code", placeholder: "30301", col: 1 },
];

export default function CheckoutPage() {
  const { items, subtotal } = useCart();
  const [form, setForm] = useState({
    name: "", email: "", address: "", city: "", state: "", zip: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const shipping = items.length === 0 ? 0 : subtotal >= 75 ? 0 : 5.99;
  const total = subtotal + shipping;

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Your cart is empty.");
      return;
    }
    for (const field of FIELDS) {
      if (!form[field.name].trim()) {
        toast.error(`Please enter your ${field.label.toLowerCase()}.`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        customer: form,
        items: items.map((i) => ({ id: i.id, qty: i.qty })),
        origin_url: window.location.origin,
      };
      const { data } = await axios.post(`${API}/payments/checkout`, payload);
      // Redirect to Stripe-hosted checkout. Cart is cleared on the
      // confirmation page once payment is verified.
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error("Something went wrong starting checkout. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <PageLayout title="Checkout" subtitle={null}>
      <div className="max-w-5xl mx-auto px-5 py-12 md:py-16">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 font-serif-body text-[#768487] hover:text-[#0f9fbc] transition-colors mb-8"
        >
          <ArrowLeft size={18} /> Continue shopping
        </Link>

        {items.length === 0 ? (
          <div className="text-center py-16">
            <p className="font-serif-body text-[#666] text-lg">Your cart is empty.</p>
            <Link
              to="/shop"
              className="inline-block mt-6 bg-[#0f9fbc] hover:bg-[#008da9] text-white font-serif-body px-6 py-3 rounded-[4px] transition-colors"
            >
              Browse the Shop
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Form */}
            <form onSubmit={handleSubmit}>
              <h2 className="font-serif-display text-[#222] text-xl font-bold mb-5">
                Shipping Details
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {FIELDS.map((field) => (
                  <div
                    key={field.name}
                    className={field.col === 2 ? "col-span-2" : "col-span-1"}
                  >
                    <label className="block font-serif-body text-[13px] text-[#555] mb-1">
                      {field.label}
                    </label>
                    <input
                      name={field.name}
                      type={field.type || "text"}
                      value={form[field.name]}
                      onChange={handleChange}
                      placeholder={field.placeholder}
                      className="w-full font-serif-body text-[15px] px-4 py-3 rounded-md border border-[#d7e6ea] focus:outline-none focus:ring-2 focus:ring-[#7ecbd6] focus:border-transparent"
                    />
                  </div>
                ))}
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="checkout-place-order-btn"
                className="mt-6 w-full flex items-center justify-center gap-2 bg-[#0f9fbc] hover:bg-[#008da9] disabled:opacity-70 text-white font-serif-body font-bold py-3.5 rounded-[4px] transition-colors"
              >
                {submitting && <Loader2 size={18} className="animate-spin" />}
                {submitting ? "Redirecting to payment…" : `Pay Securely · ${fmt(total)}`}
              </button>
              <p className="font-serif-body text-[12px] text-[#999] mt-3 text-center">
                Secure payment powered by Stripe. Sales tax is calculated at payment.
              </p>
            </form>

            {/* Summary */}
            <div>
              <h2 className="font-serif-display text-[#222] text-xl font-bold mb-5">
                Order Summary
              </h2>
              <div className="rounded-[10px] border border-[#eaeaea] p-5">
                <div className="space-y-4">
                  {items.map((i) => (
                    <div key={i.id} className="flex gap-3 items-center">
                      <img
                        src={mediaUrl(i.image)}
                        alt={i.name}
                        className="w-14 h-14 rounded-md object-cover border border-[#eaeaea] shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-serif-body text-[13px] text-[#333] line-clamp-2">
                          {i.name}
                        </p>
                        <p className="font-serif-body text-[12px] text-[#999]">Qty {i.qty}</p>
                      </div>
                      <span className="font-serif-body text-[14px] text-[#333] font-bold">
                        {fmt(i.price * i.qty)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-[#eaeaea] mt-5 pt-4 space-y-2">
                  <Row label="Subtotal" value={fmt(subtotal)} />
                  <Row label="Shipping" value={shipping === 0 ? "Free" : fmt(shipping)} />
                  <div className="flex items-center justify-between pt-2">
                    <span className="font-serif-display text-[#333] text-lg font-bold">Total</span>
                    <span className="font-serif-display text-[#0f9fbc] text-xl font-bold">
                      {fmt(total)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="font-serif-body text-[14px] text-[#666]">{label}</span>
      <span className="font-serif-body text-[14px] text-[#333]">{value}</span>
    </div>
  );
}
